import React, { createContext, useContext, useState, useCallback } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";

interface AnimationConfig {
  duration: number;
  easing: string;
  reduceMotion: boolean;
  enableWebGL?: boolean;
}

type TransitionPreset = "fade" | "slide" | "scale" | "morph";
type AnimationPreset = (typeof animationPresets)[keyof typeof animationPresets];

interface AnimationContextType {
  config: AnimationConfig;
  setConfig: (config: Partial<AnimationConfig>) => void;
}

const defaultConfig: AnimationConfig = {
  duration: 0.3,
  easing: "easeInOut",
  reduceMotion: false,
};

const AnimationContext = createContext<AnimationContextType | undefined>(
  undefined,
);

export function AnimationProvider({
  children,
  enableWebGL = false,
}: {
  children: React.ReactNode;
  enableWebGL?: boolean;
}) {
  // Check user's motion preferences
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  const [config, setConfig] = useState<AnimationConfig>({
    ...defaultConfig,
    reduceMotion: prefersReducedMotion,
    enableWebGL,
  });

  const updateConfig = useCallback((updates: Partial<AnimationConfig>) => {
    setConfig((current) => ({
      ...current,
      ...updates,
    }));
  }, []);

  return (
    <AnimationContext.Provider value={{ config, setConfig: updateConfig }}>
      <LazyMotion features={domAnimation} strict>
        {children}
      </LazyMotion>
    </AnimationContext.Provider>
  );
}

export const animationPresets = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slideIn: {
    initial: { x: -20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 20, opacity: 0 },
  },
  scaleIn: {
    initial: { scale: 0.9, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.9, opacity: 0 },
  },
  morphIn: {
    initial: { borderRadius: "50%", scale: 0 },
    animate: { borderRadius: "0%", scale: 1 },
    exit: { borderRadius: "50%", scale: 0 },
  },
};

export const Motion = {
  Div: m.div,
  Button: m.button,
  Span: m.span,
  List: m.ul,
  Item: m.li,
};

export { AnimatePresence };

export function useAnimation() {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error("useAnimation must be used within AnimationProvider");
  }
  return context;
}
